/**
 * Pure helpers for release channels in the signed list. A channel maps to
 * the release id currently live on it (`manifest.channels`), and the list
 * names one channel as its default (`manifest.defaultChannel`).
 */
import { CHANNEL_LABELS, STAGING_APP_URL } from './config'
import {
  DEFAULT_PIN_KEEP_COUNT,
  type ReleaseEntry,
  selectReleasesToKeep,
  type VersionManifest,
} from '../../../scripts/ipfs-release/schema'

/**
 * Every channel the list defines, the default channel first, then the rest
 * in alphabetical order — a stable order, so the toggle never reshuffles
 * between visits.
 */
export const listChannels = (manifest: VersionManifest): string[] => {
  const others = Object.keys(manifest.channels)
    .filter((channel) => channel !== manifest.defaultChannel)
    .sort()
  return [manifest.defaultChannel, ...others]
}

/** The release live on `channel`, or `undefined` when the list has no such channel or release. */
export const findChannelRelease = (
  manifest: VersionManifest,
  channel: string,
): ReleaseEntry | undefined => {
  const releaseId = manifest.channels[channel]
  if (releaseId === undefined) {
    return undefined
  }
  return manifest.releases.find((release) => release.id === releaseId)
}

/** Every release except `shownRelease`, in the list's own order (newest first). */
export const findOtherReleases = (
  manifest: VersionManifest,
  shownRelease: ReleaseEntry,
): ReleaseEntry[] =>
  selectReleasesToKeep(manifest, DEFAULT_PIN_KEEP_COUNT).filter((release) => release.id !== shownRelease.id)

/** The name a reader sees for `channel`: its configured label, else the channel name itself. */
export const channelLabel = (channel: string): string => CHANNEL_LABELS[channel] ?? channel

/**
 * The one-click link to the app for the channel shown.
 *
 * Production follows `master`. The default channel opens the address of the
 * method the reader chose (`methodUrl`). The caller passes the IPFS content
 * address of the shown release when the reader has chosen nothing. Another channel is staging: it opens `next.gibs.finance`, which
 * runs the staging release on its own origin. The reader's method does not
 * apply there. A reader who picked Stable is never one click from Beta.
 */
export const buildOpenAppUrl = (
  channel: string,
  defaultChannel: string,
  methodUrl: string,
): string => (channel === defaultChannel ? methodUrl : STAGING_APP_URL)
